/**
 * Test Data for Temporal Parking
 * Multiple test scenarios for validating algorithm behavior
 */

import { makeEmptyMask, timeStringToBucket } from "./bitmap";
import { Orientation, ParkingRequest, Spot, Zone } from "./types";
import { Shape } from "../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";

/**
 * Create minimal test Shape for a spot
 * Used to provide required shape field for test data
 */
function createMinimalShape(id: string): Shape {
  return {
    id,
    fbo_id: "test-fbo",
    ramp_id: "test-ramp",
    tags: [],
    points: [
      { x: 0, y: 0 },
      { x: 100, y: 0 },
      { x: 100, y: 100 },
      { x: 0, y: 100 },
    ],
    type: "Spot",
    locked: false,
    reference_points: [{ name: "center", validOrientations: ["nose_to_line"] }],
  };
}

// ==== Orientations ====

export const NOSE_IN: Orientation = {
  id: "nose_in",
  label: "Nose In",
};

// ==== Ramp Layout Configurations ====
// Different spot/zone configurations for different test scenarios

type RampLayout =
  | "minimal"
  | "small"
  | "size-constrained"
  | "medium"
  | "teb"
  | "gap-demo";

interface LayoutConfig {
  zones: Zone[];
  spots: Spot[];
}

// MINIMAL: 2 spots (for testing forced splits with absolute minimum capacity)
const LAYOUT_MINIMAL: LayoutConfig = {
  zones: [{ id: "A", name: "Row A", spotIds: ["A01", "A02"] }],
  spots: [
    {
      id: "A01",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: ["very-small", "small"],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A02"],
        },
      ],
      shape: createMinimalShape("A01"),
    },
    {
      id: "A02",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: ["small", "midsize"],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("A02"),
    },
  ],
};

// SMALL: 5 spots (for basic capacity testing)
const LAYOUT_SMALL: LayoutConfig = {
  zones: [
    { id: "A", name: "Row A", spotIds: ["A01", "A02", "A03"] },
    { id: "B", name: "Row B", spotIds: ["B01", "B02"] },
  ],
  spots: [
    {
      id: "A01",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A02", "A03"],
        },
      ],
      shape: createMinimalShape("A01"),
    },
    {
      id: "A02",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A03"],
        },
      ],
      shape: createMinimalShape("A02"),
    },
    {
      id: "A03",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("A03"),
    },
    {
      id: "B01",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["B02"],
        },
      ],
      shape: createMinimalShape("B01"),
    },
    {
      id: "B02",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("B02"),
    },
  ],
};

// SIZE_CONSTRAINED: 5 spots with heterogeneous size restrictions (for testing Local Repair with size categories)
const LAYOUT_SIZE_CONSTRAINED: LayoutConfig = {
  zones: [
    { id: "A", name: "Row A", spotIds: ["A01", "A02", "A03"] },
    { id: "B", name: "Row B", spotIds: ["B01", "B02"] },
  ],
  spots: [
    {
      id: "A01",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: ["small", "midsize"],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A02", "A03"],
        },
      ],
      shape: createMinimalShape("A01"),
    },
    {
      id: "A02",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: ["small", "midsize"],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A03"],
        },
      ],
      shape: createMinimalShape("A02"),
    },
    {
      id: "A03",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: ["very-small", "small"],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("A03"),
    },
    {
      id: "B01",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: ["large"],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["B02"],
        },
      ],
      shape: createMinimalShape("B01"),
    },
    {
      id: "B02",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: ["super-midsize", "large"],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("B02"),
    },
  ],
};

// MEDIUM: 7 spots (for moderate complexity scenarios)
const LAYOUT_MEDIUM: LayoutConfig = {
  zones: [
    { id: "A", name: "Row A", spotIds: ["A01", "A02", "A03", "A04"] },
    { id: "B", name: "Row B", spotIds: ["B01", "B02", "B03"] },
  ],
  spots: [
    {
      id: "A01",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A02", "A03", "A04"],
        },
      ],
      shape: createMinimalShape("A01"),
    },
    {
      id: "A02",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A03", "A04"],
        },
      ],
      shape: createMinimalShape("A02"),
    },
    {
      id: "A03",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A04"],
        },
      ],
      shape: createMinimalShape("A03"),
    },
    {
      id: "A04",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("A04"),
    },
    {
      id: "B01",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["B02", "B03"],
        },
      ],
      shape: createMinimalShape("B01"),
    },
    {
      id: "B02",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["B03"],
        },
      ],
      shape: createMinimalShape("B02"),
    },
    {
      id: "B03",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("B03"),
    },
  ],
};

// TEB: 24 spots (realistic large FBO layout)
const LAYOUT_TEB: LayoutConfig = {
  zones: [
    {
      id: "A",
      name: "North Row",
      spotIds: ["A01", "A02", "A03", "A04", "A05", "A06"],
    },
    {
      id: "B",
      name: "Middle-North Row",
      spotIds: ["B01", "B02", "B03", "B04", "B05", "B06"],
    },
    {
      id: "C",
      name: "Middle-South Row",
      spotIds: ["C01", "C02", "C03", "C04", "C05", "C06"],
    },
    {
      id: "D",
      name: "South Row",
      spotIds: ["D01", "D02", "D03", "D04", "D05", "D06"],
    },
  ],
  spots: [
    // North Row (A) - Uses Lane 1 - A01 blocks A02-A06, A02 blocks A03-A06, etc.
    {
      id: "A01",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A02", "A03", "A04", "A05", "A06"],
        },
      ],
      shape: createMinimalShape("A01"),
    },
    {
      id: "A02",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A03", "A04", "A05", "A06"],
        },
      ],
      shape: createMinimalShape("A02"),
    },
    {
      id: "A03",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A04", "A05", "A06"],
        },
      ],
      shape: createMinimalShape("A03"),
    },
    {
      id: "A04",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A05", "A06"],
        },
      ],
      shape: createMinimalShape("A04"),
    },
    {
      id: "A05",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["A06"],
        },
      ],
      shape: createMinimalShape("A05"),
    },
    {
      id: "A06",
      zoneId: "A",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("A06"),
    },
    // Middle-North Row (B) - Uses Lane 1 or Lane 2
    {
      id: "B01",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["B02", "B03", "B04", "B05", "B06"],
        },
      ],
      shape: createMinimalShape("B01"),
    },
    {
      id: "B02",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["B03", "B04", "B05", "B06"],
        },
      ],
      shape: createMinimalShape("B02"),
    },
    {
      id: "B03",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: ["B04", "B05", "B06"],
        },
      ],
      shape: createMinimalShape("B03"),
    },
    {
      id: "B04",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["B05", "B06"],
        },
      ],
      shape: createMinimalShape("B04"),
    },
    {
      id: "B05",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["B06"],
        },
      ],
      shape: createMinimalShape("B05"),
    },
    {
      id: "B06",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("B06"),
    },
    // Middle-South Row (C) - Uses Lane 2
    {
      id: "C01",
      zoneId: "C",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["C02", "C03", "C04", "C05", "C06"],
        },
      ],
      shape: createMinimalShape("C01"),
    },
    {
      id: "C02",
      zoneId: "C",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["C03", "C04", "C05", "C06"],
        },
      ],
      shape: createMinimalShape("C02"),
    },
    {
      id: "C03",
      zoneId: "C",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["C04", "C05", "C06"],
        },
      ],
      shape: createMinimalShape("C03"),
    },
    {
      id: "C04",
      zoneId: "C",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["C05", "C06"],
        },
      ],
      shape: createMinimalShape("C04"),
    },
    {
      id: "C05",
      zoneId: "C",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: ["C06"],
        },
      ],
      shape: createMinimalShape("C05"),
    },
    {
      id: "C06",
      zoneId: "C",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_2",
          laneName: "Lane 2",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("C06"),
    },
    // South Row (D) - Uses Lane 3
    {
      id: "D01",
      zoneId: "D",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_3",
          laneName: "Lane 3",
          blocksSpotIds: ["D02", "D03", "D04", "D05", "D06"],
        },
      ],
      shape: createMinimalShape("D01"),
    },
    {
      id: "D02",
      zoneId: "D",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_3",
          laneName: "Lane 3",
          blocksSpotIds: ["D03", "D04", "D05", "D06"],
        },
      ],
      shape: createMinimalShape("D02"),
    },
    {
      id: "D03",
      zoneId: "D",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_3",
          laneName: "Lane 3",
          blocksSpotIds: ["D04", "D05", "D06"],
        },
      ],
      shape: createMinimalShape("D03"),
    },
    {
      id: "D04",
      zoneId: "D",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_3",
          laneName: "Lane 3",
          blocksSpotIds: ["D05", "D06"],
        },
      ],
      shape: createMinimalShape("D04"),
    },
    {
      id: "D05",
      zoneId: "D",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_3",
          laneName: "Lane 3",
          blocksSpotIds: ["D06"],
        },
      ],
      shape: createMinimalShape("D05"),
    },
    {
      id: "D06",
      zoneId: "D",
      orientations: [NOSE_IN],
      allowedSizes: [
        "very-small",
        "small",
        "midsize",
        "super-midsize",
        "large",
      ],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_3",
          laneName: "Lane 3",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("D06"),
    },
  ],
};

// GAP_DEMO: 3 spots demonstrating gap spot behavior
// B01 and B02 are large spots side-by-side
// B06 is a small gap spot between them (Phase 2a - just the flag, no dependencies yet)
const LAYOUT_GAP_DEMO: LayoutConfig = {
  zones: [{ id: "B", name: "Row B", spotIds: ["B01", "B02", "B06"] }],
  spots: [
    // Regular large spots (side by side)
    {
      id: "B01",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: ["large", "super-midsize"],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("B01"),
    },
    {
      id: "B02",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: ["large", "super-midsize"],
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: [],
        },
      ],
      shape: createMinimalShape("B02"),
    },

    // Gap spot - Phase 2c/2d: with requiresOccupiedSpots
    // Small aircraft can fit in the space between two large aircraft
    // Only available when BOTH B01 and B02 are occupied
    {
      id: "B06",
      zoneId: "B",
      orientations: [NOSE_IN],
      allowedSizes: ["very-small", "small"], // Only small aircraft fit
      occupancy: makeEmptyMask(),
      egressOptions: [
        {
          laneId: "lane_1",
          laneName: "Lane 1",
          blocksSpotIds: [],
        },
      ],
      isGapSpot: true, // Gap spot flag (Phase 2a)
      requiresOccupiedSpots: ["B01", "B02"], // Required spots (Phase 2c/2d)
      shape: createMinimalShape("B06"),
    },
  ],
};

const LAYOUTS: Record<RampLayout, LayoutConfig> = {
  minimal: LAYOUT_MINIMAL,
  small: LAYOUT_SMALL,
  "size-constrained": LAYOUT_SIZE_CONSTRAINED,
  medium: LAYOUT_MEDIUM,
  teb: LAYOUT_TEB,
  "gap-demo": LAYOUT_GAP_DEMO,
};

// ==== Test Scenario Types ====

/**
 * Aircraft definition for test data using human-readable time strings
 * These get converted to buckets during initialization
 */
type TestAircraft = {
  id: string;
  tailNumber: string;
  sizeCategory: string; // Aircraft size: "very-small", "small", "midsize", "super-midsize", "large"
  arrival: string | number; // Format: "HH:MM" (e.g., "08:30") or bucket number for multi-day
  departure: string | number; // Format: "HH:MM" (e.g., "14:45") or bucket number for multi-day
  locks?: Array<{
    spotId: string;
    startBucket: number;
    endBucket: number;
    reason?: string;
  }>;
};

/**
 * Test scenario definition with time strings
 */
type TestScenarioDefinition = {
  id: string;
  name: string;
  description: string;
  rampLayout: RampLayout;
  aircraft: TestAircraft[];
  expectedPlaced: number;
  expectedUnplaced: number;
  startTime: string; // Planning window start time (e.g., "00:00", "06:00", "18:00")
  horizonHours: number; // Planning window duration in hours (e.g., 12, 24, 48, 72)
};

/**
 * Runtime test scenario with converted bucket times
 */
export type TestScenario = {
  id: string;
  name: string;
  description: string;
  rampLayout: RampLayout;
  aircraft: ParkingRequest[];
  expectedPlaced: number;
  expectedUnplaced: number;
  startTime: string; // Planning window start time (e.g., "00:00", "06:00", "18:00")
  horizonHours: number; // Planning window duration in hours (e.g., 12, 24, 48, 72)
};

// ==== Test Scenarios ====
// Defined with human-readable time strings, converted to buckets at runtime

const TEST_SCENARIO_DEFINITIONS: TestScenarioDefinition[] = [
  {
    id: "basic",
    name: "Basic - Balanced Load",
    description:
      "5 aircraft, 5 spots. All should fit comfortably with good distribution.",
    rampLayout: "small",
    aircraft: [
      {
        id: "ac1",
        tailNumber: "N650GL",
        sizeCategory: "small",
        arrival: "00:00",
        departure: "04:40",
      },
      {
        id: "ac2",
        tailNumber: "N88GX",
        sizeCategory: "small",
        arrival: "00:20",
        departure: "09:55",
      },
      {
        id: "ac3",
        tailNumber: "N7XXF",
        sizeCategory: "small",
        arrival: "00:30",
        departure: "16:00",
      },
      {
        id: "ac4",
        tailNumber: "N345QS",
        sizeCategory: "small",
        arrival: "00:45",
        departure: "02:25",
      },
      {
        id: "ac5",
        tailNumber: "N23PC",
        sizeCategory: "small",
        arrival: "01:10",
        departure: "07:00",
      },
    ],
    expectedPlaced: 5,
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "oversubscribed",
    name: "Oversubscribed - Capacity Exceeded",
    description:
      "8 aircraft, 5 spots with overlapping times. Tests unplaced aircraft handling.",
    rampLayout: "small",
    aircraft: [
      {
        id: "ac1",
        tailNumber: "N101",
        sizeCategory: "small",
        arrival: "00:00",
        departure: "09:00",
      },
      {
        id: "ac2",
        tailNumber: "N102",
        sizeCategory: "small",
        arrival: "00:15",
        departure: "08:45",
      },
      {
        id: "ac3",
        tailNumber: "N103",
        sizeCategory: "small",
        arrival: "00:30",
        departure: "08:30",
      },
      {
        id: "ac4",
        tailNumber: "N104",
        sizeCategory: "small",
        arrival: "00:45",
        departure: "08:15",
      },
      {
        id: "ac5",
        tailNumber: "N105",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "08:00",
      },
      {
        id: "ac6",
        tailNumber: "N106",
        sizeCategory: "small",
        arrival: "01:15",
        departure: "07:45",
      },
      {
        id: "ac7",
        tailNumber: "N107",
        sizeCategory: "small",
        arrival: "01:30",
        departure: "07:30",
      },
      {
        id: "ac8",
        tailNumber: "N108",
        sizeCategory: "small",
        arrival: "01:45",
        departure: "07:15",
      },
    ],
    expectedPlaced: 5,
    expectedUnplaced: 3,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "size-constrained",
    name: "Size-Constrained Shuffle",
    description:
      "6 aircraft (2 large + 1 super-midsize) competing for 2 B-row spots (B01=large-only, B02=flex). Shows importance of sorting strategy. Capacity constraint: impossible to place all 6 (3 aircraft need 2 spots).",
    rampLayout: "size-constrained",
    aircraft: [
      {
        id: "ac1",
        tailNumber: "N100",
        sizeCategory: "large",
        arrival: "08:00",
        departure: "16:00",
      },
      {
        id: "ac2",
        tailNumber: "N200",
        sizeCategory: "small",
        arrival: "08:15",
        departure: "12:00",
      },
      {
        id: "ac3",
        tailNumber: "N300",
        sizeCategory: "midsize",
        arrival: "08:30",
        departure: "14:00",
      },
      {
        id: "ac4",
        tailNumber: "N400",
        sizeCategory: "super-midsize",
        arrival: "09:00",
        departure: "17:00",
      },
      {
        id: "ac5",
        tailNumber: "N500",
        sizeCategory: "small",
        arrival: "10:00",
        departure: "15:00",
      },
      {
        id: "ac6",
        tailNumber: "N600",
        sizeCategory: "large",
        arrival: "11:00",
        departure: "18:00",
      },
    ],
    expectedPlaced: 5, // Maximum possible: N100+N400 need B01/B02, but 3 aircraft (N100, N400, N600) compete for 2 spots
    expectedUnplaced: 1,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "waste-demo",
    name: "Waste Demo - Size Mismatch",
    description:
      "Demonstrates waste scoring with mixed sizes competing for limited spots. With waste disabled, small aircraft may grab premium large spots early, leaving large aircraft stranded. With waste high/very high, small aircraft avoid oversized spots, saving capacity for aircraft that need it.",
    rampLayout: "size-constrained",
    aircraft: [
      // Large aircraft arrives first - NEEDS B01 or B02 (only spots that fit large)
      {
        id: "ac1",
        tailNumber: "N700L",
        sizeCategory: "large",
        arrival: "08:00",
        departure: "16:00",
      },
      // Small aircraft arrive early - CAN fit B01/B02 but waste space (waste=3)
      {
        id: "ac2",
        tailNumber: "N701S",
        sizeCategory: "small",
        arrival: "08:05",
        departure: "12:00",
      },
      {
        id: "ac3",
        tailNumber: "N702S",
        sizeCategory: "small",
        arrival: "08:10",
        departure: "13:00",
      },
      // Very-small arrives - best fit is A03 (waste=0), okay fit A01/A02 (waste=1)
      {
        id: "ac4",
        tailNumber: "N703VS",
        sizeCategory: "very-small",
        arrival: "08:15",
        departure: "11:00",
      },
      // Midsize arrives - fits A01/A02 (waste=0), or B02 (waste=1)
      {
        id: "ac5",
        tailNumber: "N704M",
        sizeCategory: "midsize",
        arrival: "08:20",
        departure: "14:00",
      },
    ],
    expectedPlaced: 5, // All should fit with smart placement
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "longterm",
    name: "Long-term Parkers - Overnight Mix",
    description:
      "7 aircraft on 7 spots. 3 long parkers + 4 quick turns. Tests gap filling between long dwells.",
    rampLayout: "medium",
    aircraft: [
      // Long-term parkers
      {
        id: "ac1",
        tailNumber: "N401L",
        sizeCategory: "small",
        arrival: "00:00",
        departure: "14:00",
      },
      {
        id: "ac2",
        tailNumber: "N402L",
        sizeCategory: "small",
        arrival: "00:30",
        departure: "14:30",
      },
      {
        id: "ac3",
        tailNumber: "N403L",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "15:00",
      },
      // Quick turns
      {
        id: "ac4",
        tailNumber: "N404Q",
        sizeCategory: "small",
        arrival: "02:00",
        departure: "03:30",
      },
      {
        id: "ac5",
        tailNumber: "N405Q",
        sizeCategory: "small",
        arrival: "04:00",
        departure: "05:30",
      },
      {
        id: "ac6",
        tailNumber: "N406Q",
        sizeCategory: "small",
        arrival: "07:00",
        departure: "08:30",
      },
      {
        id: "ac7",
        tailNumber: "N407Q",
        sizeCategory: "small",
        arrival: "10:00",
        departure: "11:30",
      },
    ],
    expectedPlaced: 7,
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "buffer-sequential",
    name: "Sequential Reuse - Buffer Gap Test",
    description:
      "6 aircraft on 7 spots with 2-hour dwells and exact handoffs. With 0 buffer all share 1 spot; with 15min buffer they overlap and need multiple spots. Watch the timeline for gaps!",
    rampLayout: "medium",
    aircraft: [
      // Perfect 2-hour sequential blocks - no buffer = perfect reuse
      {
        id: "ac1",
        tailNumber: "N601",
        sizeCategory: "small",
        arrival: "00:00",
        departure: "02:00",
      },
      {
        id: "ac2",
        tailNumber: "N602",
        sizeCategory: "small",
        arrival: "02:00",
        departure: "04:00",
      },
      {
        id: "ac3",
        tailNumber: "N603",
        sizeCategory: "small",
        arrival: "04:00",
        departure: "06:00",
      },
      {
        id: "ac4",
        tailNumber: "N604",
        sizeCategory: "small",
        arrival: "06:00",
        departure: "08:00",
      },
      {
        id: "ac5",
        tailNumber: "N605",
        sizeCategory: "small",
        arrival: "08:00",
        departure: "10:00",
      },
      {
        id: "ac6",
        tailNumber: "N606",
        sizeCategory: "small",
        arrival: "10:00",
        departure: "12:00",
      },
    ],
    // With 0 buffer: all 6 fit in 1 spot (perfect sequential reuse)
    // With 15min buffer: buffer creates overlaps, need 2-3 spots
    expectedPlaced: 6, // all fit, but spread across multiple spots with buffer
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "buffer-capacity",
    name: "Capacity Reduction - Buffer Test",
    description:
      "8 aircraft on 5 spots arriving in 1-hour rush, all leaving 9 hours later. Buffer reduces effective capacity. Watch unplaced count increase with buffer!",
    rampLayout: "small",
    aircraft: [
      // All arrive 00:00-01:00, all depart 09:00-10:00
      // With 0 buffer: arrival times don't overlap much, 5 fit
      // With 15min+ buffer: arrival buffers overlap heavily, fewer fit
      {
        id: "ac1",
        tailNumber: "N611",
        sizeCategory: "small",
        arrival: "00:00",
        departure: "09:00",
      },
      {
        id: "ac2",
        tailNumber: "N612",
        sizeCategory: "small",
        arrival: "00:10",
        departure: "09:10",
      },
      {
        id: "ac3",
        tailNumber: "N613",
        sizeCategory: "small",
        arrival: "00:20",
        departure: "09:20",
      },
      {
        id: "ac4",
        tailNumber: "N614",
        sizeCategory: "small",
        arrival: "00:30",
        departure: "09:30",
      },
      {
        id: "ac5",
        tailNumber: "N615",
        sizeCategory: "small",
        arrival: "00:40",
        departure: "09:40",
      },
      {
        id: "ac6",
        tailNumber: "N616",
        sizeCategory: "small",
        arrival: "00:50",
        departure: "09:50",
      },
      {
        id: "ac7",
        tailNumber: "N617",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "10:00",
      },
      {
        id: "ac8",
        tailNumber: "N618",
        sizeCategory: "small",
        arrival: "01:10",
        departure: "10:10",
      },
    ],
    // With 0 buffer: 5 placed (fills all spots)
    // With 15min buffer: 5 placed (but arrival window overlaps more)
    expectedPlaced: 5,
    expectedUnplaced: 3,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "split-required",
    name: "Skip-Ahead Demonstration",
    description:
      "6 aircraft on 5 spots. Last aircraft arrives at 02:00 when all spots occupied, algorithm skips ahead to 04:00 (delayed placement). **Use 0 buffer, Allow 1 Move**.",
    rampLayout: "small",
    aircraft: [
      // First 3: 01:00-07:00 (occupy 3 spots fully)
      {
        id: "ac1",
        tailNumber: "N701",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "07:00",
      },
      {
        id: "ac2",
        tailNumber: "N702",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "07:00",
      },
      {
        id: "ac3",
        tailNumber: "N703",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "07:00",
      },

      // 4th: 01:00-04:00 (occupies spot for morning only)
      {
        id: "ac4",
        tailNumber: "N704",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "04:00",
      },

      // 5th: 01:00-04:00 (occupies last spot for morning)
      {
        id: "ac5",
        tailNumber: "N705",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "04:00",
      },

      // 6th: 02:00-07:00 - arrives when all spots occupied, demonstrates skip-ahead
      {
        id: "ac6",
        tailNumber: "N706",
        sizeCategory: "small",
        arrival: "02:00",
        departure: "07:00",
      },

      // After first 5 placed:
      // A01/A02/A03: OCCUPIED 01-07 (long dwells)
      // B01/B02: OCCUPIED 01-04 (short dwells), FREE 04-07
      //
      // N706 (arrives 02:00, needs 02-07):
      // - All spots occupied at 02:00
      // - Skip-ahead finds B01 free at 04:00
      // - Places N706 at B01 from 04-07 (delayed start)
      // - Shows as ⚠️ partial placement (60% coverage)
    ],
    expectedPlaced: 6, // With plannedMoveCap >= 0, N706 gets delayed placement
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "multi-spot-split",
    name: "Multi-Spot Attempt",
    description:
      "7 aircraft on 7 spots. Attempts to force multi-spot split. Greedy algorithm may still find single-spot placement. **Use 15min buffer, Allow 1 Move**.",
    rampLayout: "medium",
    aircraft: [
      // Fill A01/A02/A03 all day
      {
        id: "ac1",
        tailNumber: "N801",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "09:00",
      },
      {
        id: "ac2",
        tailNumber: "N802",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "09:00",
      },
      {
        id: "ac3",
        tailNumber: "N803",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "09:00",
      },

      // B01: Morning until 06:00
      {
        id: "ac4",
        tailNumber: "N804",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "06:00",
      },

      // B02: Morning until 04:00
      {
        id: "ac5",
        tailNumber: "N805",
        sizeCategory: "small",
        arrival: "01:00",
        departure: "04:00",
      },

      // B02: Afternoon from 06:30 (leaves gap with buffer)
      {
        id: "ac6",
        tailNumber: "N806",
        sizeCategory: "small",
        arrival: "06:30",
        departure: "09:00",
      },

      // Last: 04:00-09:00, may split or find single spot
      {
        id: "ac7",
        tailNumber: "N807",
        sizeCategory: "small",
        arrival: "04:00",
        departure: "09:00",
      },

      // With 15min buffer:
      // B01: 00:45-06:15 (N804), FREE 06:15-09:00
      // B02: 00:45-04:15 (N805), FREE 04:15-06:15, 06:15-09:15 (N806)
      // N807: 03:45-09:15 needed
      // Greedy algorithm will likely find a single-spot solution
    ],
    expectedPlaced: 7,
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "minimal-split",
    name: "Minimal 2-Spot Split Attempt",
    description:
      "3 aircraft on 2 spots. Attempts to force multi-spot split with absolute minimum capacity. **Use 0 buffer, Allow 1 Move**. Note: Greedy algorithm may still find single-spot solutions depending on placement order.",
    rampLayout: "minimal",
    aircraft: [
      // Design intent: Create complementary gaps in A01 and A02 that force N903 to split
      // A01 blocked late (03:00-06:00), A02 blocked early (00:00-02:00)
      // N903 needs 00:00-06:00, theoretically requiring A01(00-03) → A02(03-06)

      // Block A01 from 03:00-06:00
      {
        id: "ac1",
        tailNumber: "N901",
        sizeCategory: "small",
        arrival: "03:00",
        departure: "06:00",
      },

      // Block A02 from 00:00-02:00
      {
        id: "ac2",
        tailNumber: "N902",
        sizeCategory: "small",
        arrival: "00:00",
        departure: "02:00",
      },

      // Target: needs 00:00-06:00
      // Theory: Must split across A01(00-03) → A02(03-06) with 1 move at 03:00
      // Reality: Greedy placement order may allow single-spot placement if N903 claims
      //          a spot before the blocking aircraft reserve their windows
      {
        id: "ac3",
        tailNumber: "N903",
        sizeCategory: "small",
        arrival: "00:00",
        departure: "06:00",
      },

      // WHY SPLITS ARE HARD TO TEST:
      // The greedy algorithm places aircraft in arrival order and always picks the
      // longest available window from the current bucket position. It only creates
      // multi-spot splits when NO single spot can accommodate the full dwell.
      //
      // In test scenarios with "clean" initial states, the algorithm has maximum
      // flexibility and usually finds single-spot solutions. True multi-spot splits
      // are more likely in real-world scenarios with:
      // - Immovable aircraft already on the ramp (pre-existing occupancy)
      // - Spot priorities/preferences (certain spots restricted for certain aircraft)
      // - Real-time capacity constraints (irregular arrival clustering)
      // - Complex multi-day planning windows
      //
      // This scenario demonstrates the segmentation infrastructure is working, even
      // if it doesn't consistently trigger 2-segment placements in the test environment.
    ],
    expectedPlaced: 3,
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "busy-friday",
    name: "Busy Friday - Large Scale",
    description:
      "Realistic busy Friday scenario with 15 aircraft on 7 spots. Mix of long-dwell heavies and quick transient turns. **Use 15min buffer, Allow 1 Move**.",
    rampLayout: "medium",
    aircraft: [
      // Morning rush - Long dwells (heavies staying most of day)
      {
        id: "ac1",
        tailNumber: "N650GL",
        sizeCategory: "small",
        arrival: "07:50",
        departure: "12:30",
      },
      {
        id: "ac2",
        tailNumber: "N88GX",
        sizeCategory: "small",
        arrival: "08:10",
        departure: "17:45",
      },
      {
        id: "ac3",
        tailNumber: "N7XXF",
        sizeCategory: "small",
        arrival: "08:30",
        departure: "16:00",
      },

      // Mid-morning - Quick turns
      {
        id: "ac4",
        tailNumber: "N345QS",
        sizeCategory: "small",
        arrival: "09:45",
        departure: "11:25",
      },
      {
        id: "ac5",
        tailNumber: "N23PC",
        sizeCategory: "small",
        arrival: "10:10",
        departure: "14:00",
      },

      // Lunch rush - More long dwells
      {
        id: "ac6",
        tailNumber: "N501ZR",
        sizeCategory: "small",
        arrival: "11:30",
        departure: "19:15",
      },
      {
        id: "ac7",
        tailNumber: "N678PH",
        sizeCategory: "small",
        arrival: "12:00",
        departure: "18:30",
      },

      // Afternoon - Mixed
      {
        id: "ac8",
        tailNumber: "N42TF",
        sizeCategory: "small",
        arrival: "13:15",
        departure: "15:45",
      },
      {
        id: "ac9",
        tailNumber: "N890LX",
        sizeCategory: "small",
        arrival: "14:20",
        departure: "20:00",
      },
      {
        id: "ac10",
        tailNumber: "N111SK",
        sizeCategory: "small",
        arrival: "15:00",
        departure: "17:30",
      },

      // Evening rush - Overnight parkers
      {
        id: "ac11",
        tailNumber: "N222FJ",
        sizeCategory: "small",
        arrival: "16:45",
        departure: "23:00",
      },
      {
        id: "ac12",
        tailNumber: "N333GT",
        sizeCategory: "small",
        arrival: "17:30",
        departure: "22:15",
      },
      {
        id: "ac13",
        tailNumber: "N444BR",
        sizeCategory: "small",
        arrival: "18:00",
        departure: "21:45",
      },

      // Late evening
      {
        id: "ac14",
        tailNumber: "N555CX",
        sizeCategory: "small",
        arrival: "19:30",
        departure: "23:45",
      },
      {
        id: "ac15",
        tailNumber: "N666MV",
        sizeCategory: "small",
        arrival: "20:15",
        departure: "23:30",
      },
    ],
    // With 15min buffer and 24 spots, all aircraft should easily fit
    expectedPlaced: 15,
    expectedUnplaced: 0,
    startTime: "06:00",
    horizonHours: 18,
  },
  {
    id: "teb-schedule",
    name: "TEB Schedule - Real Data (36 Aircraft)",
    description:
      "Real-world schedule from Teterboro (TEB) on 09/24/2025, 06:00-22:00. ALL 36 aircraft with actual arrival/departure times on 24 spots (4 rows × 6). **Use 15min buffer, Allow 1 Move**.",
    rampLayout: "teb",
    aircraft: [
      // Data source: plans/schedule-teb.txt
      // Time window: 09/24/2025 06:00 - 22:00 (16 hour operational window)
      // Times relative to 06:00 start
      {
        id: "ac1",
        tailNumber: "N132QS",
        sizeCategory: "small",
        arrival: "06:48",
        departure: "11:00",
      },
      {
        id: "ac2",
        tailNumber: "N822QS",
        sizeCategory: "small",
        arrival: "07:06",
        departure: "09:00",
      },
      {
        id: "ac3",
        tailNumber: "N682QS",
        sizeCategory: "small",
        arrival: "07:33",
        departure: "10:48",
      },
      {
        id: "ac4",
        tailNumber: "N697QS",
        sizeCategory: "small",
        arrival: "09:24",
        departure: "10:30",
      },
      {
        id: "ac5",
        tailNumber: "N478QS",
        sizeCategory: "small",
        arrival: "09:33",
        departure: "16:00",
      },
      {
        id: "ac6",
        tailNumber: "N523QS",
        sizeCategory: "small",
        arrival: "09:48",
        departure: "11:00",
      },
      {
        id: "ac7",
        tailNumber: "N839QS",
        sizeCategory: "small",
        arrival: "10:09",
        departure: "15:09",
      },
      {
        id: "ac8",
        tailNumber: "N791QS",
        sizeCategory: "small",
        arrival: "10:18",
        departure: "11:18",
      },
      {
        id: "ac9",
        tailNumber: "N336QS",
        sizeCategory: "small",
        arrival: "11:09",
        departure: "17:09",
      },
      {
        id: "ac10",
        tailNumber: "N866QS",
        sizeCategory: "small",
        arrival: "11:18",
        departure: "16:00",
      },
      {
        id: "ac11",
        tailNumber: "N313QS",
        sizeCategory: "small",
        arrival: "11:30",
        departure: "12:30",
      },
      {
        id: "ac12",
        tailNumber: "N957QS",
        sizeCategory: "small",
        arrival: "11:30",
        departure: "13:09",
      },
      {
        id: "ac13",
        tailNumber: "N525QS",
        sizeCategory: "small",
        arrival: "11:48",
        departure: "15:45",
      },
      {
        id: "ac14",
        tailNumber: "N881QS",
        sizeCategory: "small",
        arrival: "12:48",
        departure: "13:48",
      },
      {
        id: "ac15",
        tailNumber: "N91GV",
        sizeCategory: "small",
        arrival: "13:00",
        departure: "14:00",
      },
      {
        id: "ac16",
        tailNumber: "N922QS",
        sizeCategory: "small",
        arrival: "13:06",
        departure: "15:30",
      },
      {
        id: "ac17",
        tailNumber: "N835QS",
        sizeCategory: "small",
        arrival: "13:33",
        departure: "15:00",
      },
      {
        id: "ac18",
        tailNumber: "N972QS",
        sizeCategory: "small",
        arrival: "13:54",
        departure: "14:54",
      },
      {
        id: "ac19",
        tailNumber: "N856QS",
        sizeCategory: "small",
        arrival: "14:36",
        departure: "18:00",
      },
      {
        id: "ac20",
        tailNumber: "N791QS",
        sizeCategory: "small",
        arrival: "14:48",
        departure: "17:00",
      },
      {
        id: "ac21",
        tailNumber: "N677QS",
        sizeCategory: "small",
        arrival: "15:09",
        departure: "17:00",
      },
      {
        id: "ac22",
        tailNumber: "N731QS",
        sizeCategory: "small",
        arrival: "15:54",
        departure: "19:30",
      },
      {
        id: "ac23",
        tailNumber: "N604QS",
        sizeCategory: "small",
        arrival: "15:57",
        departure: "21:09",
      },
      {
        id: "ac24",
        tailNumber: "N410LG",
        sizeCategory: "small",
        arrival: "16:03",
        departure: "17:03",
      },
      {
        id: "ac25",
        tailNumber: "N189QS",
        sizeCategory: "small",
        arrival: "16:21",
        departure: "19:00",
      },
      {
        id: "ac26",
        tailNumber: "N348QS",
        sizeCategory: "small",
        arrival: "16:24",
        departure: "20:09",
      },
      {
        id: "ac27",
        tailNumber: "N755QS",
        sizeCategory: "small",
        arrival: "16:42",
        departure: "17:00",
      },
      {
        id: "ac28",
        tailNumber: "N750QS",
        sizeCategory: "small",
        arrival: "17:09",
        departure: "18:17",
      },
      {
        id: "ac29",
        tailNumber: "N905QS",
        sizeCategory: "small",
        arrival: "17:18",
        departure: "18:18",
      },
      {
        id: "ac30",
        tailNumber: "N635QS",
        sizeCategory: "small",
        arrival: "17:36",
        departure: "19:00",
      },
      {
        id: "ac31",
        tailNumber: "N871QS",
        sizeCategory: "small",
        arrival: "18:12",
        departure: "21:40",
      },
      {
        id: "ac32",
        tailNumber: "N570QS",
        sizeCategory: "small",
        arrival: "18:18",
        departure: "18:48",
      },
      {
        id: "ac33",
        tailNumber: "N512QS",
        sizeCategory: "small",
        arrival: "18:18",
        departure: "19:18",
      },
      {
        id: "ac34",
        tailNumber: "N373QS",
        sizeCategory: "small",
        arrival: "18:54",
        departure: "21:00",
      },
      {
        id: "ac35",
        tailNumber: "N514QS",
        sizeCategory: "small",
        arrival: "19:18",
        departure: "22:00",
      },
      {
        id: "ac36",
        tailNumber: "N160QS",
        sizeCategory: "small",
        arrival: "20:15",
        departure: "21:15",
      },
    ],
    // With 24 spots (TEB-realistic layout) and 36 aircraft with overlapping dwells, expect most to fit
    // Peak periods may still have 12-15 aircraft on ground simultaneously
    expectedPlaced: 32,
    expectedUnplaced: 4,
    startTime: "06:00",
    horizonHours: 16,
  },
  {
    id: "teb-2x",
    name: "TEB 2x - Stress Test (72 Aircraft)",
    description:
      "Extreme stress test: TEB schedule duplicated with slight offsets. 72 aircraft competing for 24 spots. Expect heavy segmentation and unplaced aircraft. **Use 15min buffer, Allow 1-2 Moves**.",
    rampLayout: "teb",
    aircraft: [
      // Original TEB schedule (36 aircraft)
      {
        id: "ac1",
        tailNumber: "N132QS",
        sizeCategory: "small",
        arrival: "06:48",
        departure: "11:00",
      },
      {
        id: "ac2",
        tailNumber: "N822QS",
        sizeCategory: "small",
        arrival: "07:06",
        departure: "09:00",
      },
      {
        id: "ac3",
        tailNumber: "N682QS",
        sizeCategory: "small",
        arrival: "07:33",
        departure: "10:48",
      },
      {
        id: "ac4",
        tailNumber: "N697QS",
        sizeCategory: "small",
        arrival: "09:24",
        departure: "10:30",
      },
      {
        id: "ac5",
        tailNumber: "N478QS",
        sizeCategory: "small",
        arrival: "09:33",
        departure: "16:00",
      },
      {
        id: "ac6",
        tailNumber: "N523QS",
        sizeCategory: "small",
        arrival: "09:48",
        departure: "11:00",
      },
      {
        id: "ac7",
        tailNumber: "N839QS",
        sizeCategory: "small",
        arrival: "10:09",
        departure: "15:09",
      },
      {
        id: "ac8",
        tailNumber: "N791QS",
        sizeCategory: "small",
        arrival: "10:18",
        departure: "11:18",
      },
      {
        id: "ac9",
        tailNumber: "N336QS",
        sizeCategory: "small",
        arrival: "11:09",
        departure: "17:09",
      },
      {
        id: "ac10",
        tailNumber: "N866QS",
        sizeCategory: "small",
        arrival: "11:18",
        departure: "16:00",
      },
      {
        id: "ac11",
        tailNumber: "N313QS",
        sizeCategory: "small",
        arrival: "11:30",
        departure: "12:30",
      },
      {
        id: "ac12",
        tailNumber: "N957QS",
        sizeCategory: "small",
        arrival: "11:30",
        departure: "13:09",
      },
      {
        id: "ac13",
        tailNumber: "N525QS",
        sizeCategory: "small",
        arrival: "11:48",
        departure: "15:45",
      },
      {
        id: "ac14",
        tailNumber: "N881QS",
        sizeCategory: "small",
        arrival: "12:48",
        departure: "13:48",
      },
      {
        id: "ac15",
        tailNumber: "N91GV",
        sizeCategory: "small",
        arrival: "13:00",
        departure: "14:00",
      },
      {
        id: "ac16",
        tailNumber: "N922QS",
        sizeCategory: "small",
        arrival: "13:06",
        departure: "15:30",
      },
      {
        id: "ac17",
        tailNumber: "N835QS",
        sizeCategory: "small",
        arrival: "13:33",
        departure: "15:00",
      },
      {
        id: "ac18",
        tailNumber: "N972QS",
        sizeCategory: "small",
        arrival: "13:54",
        departure: "14:54",
      },
      {
        id: "ac19",
        tailNumber: "N856QS",
        sizeCategory: "small",
        arrival: "14:36",
        departure: "18:00",
      },
      {
        id: "ac20",
        tailNumber: "N791QS",
        sizeCategory: "small",
        arrival: "14:48",
        departure: "17:00",
      },
      {
        id: "ac21",
        tailNumber: "N677QS",
        sizeCategory: "small",
        arrival: "15:09",
        departure: "17:00",
      },
      {
        id: "ac22",
        tailNumber: "N731QS",
        sizeCategory: "small",
        arrival: "15:54",
        departure: "19:30",
      },
      {
        id: "ac23",
        tailNumber: "N604QS",
        sizeCategory: "small",
        arrival: "15:57",
        departure: "21:09",
      },
      {
        id: "ac24",
        tailNumber: "N410LG",
        sizeCategory: "small",
        arrival: "16:03",
        departure: "17:03",
      },
      {
        id: "ac25",
        tailNumber: "N189QS",
        sizeCategory: "small",
        arrival: "16:21",
        departure: "19:00",
      },
      {
        id: "ac26",
        tailNumber: "N348QS",
        sizeCategory: "small",
        arrival: "16:24",
        departure: "20:09",
      },
      {
        id: "ac27",
        tailNumber: "N755QS",
        sizeCategory: "small",
        arrival: "16:42",
        departure: "17:00",
      },
      {
        id: "ac28",
        tailNumber: "N750QS",
        sizeCategory: "small",
        arrival: "17:09",
        departure: "18:17",
      },
      {
        id: "ac29",
        tailNumber: "N905QS",
        sizeCategory: "small",
        arrival: "17:18",
        departure: "18:18",
      },
      {
        id: "ac30",
        tailNumber: "N635QS",
        sizeCategory: "small",
        arrival: "17:36",
        departure: "19:00",
      },
      {
        id: "ac31",
        tailNumber: "N871QS",
        sizeCategory: "small",
        arrival: "18:12",
        departure: "21:40",
      },
      {
        id: "ac32",
        tailNumber: "N570QS",
        sizeCategory: "small",
        arrival: "18:18",
        departure: "18:48",
      },
      {
        id: "ac33",
        tailNumber: "N512QS",
        sizeCategory: "small",
        arrival: "18:18",
        departure: "19:18",
      },
      {
        id: "ac34",
        tailNumber: "N373QS",
        sizeCategory: "small",
        arrival: "18:54",
        departure: "21:00",
      },
      {
        id: "ac35",
        tailNumber: "N514QS",
        sizeCategory: "small",
        arrival: "19:18",
        departure: "22:00",
      },
      {
        id: "ac36",
        tailNumber: "N160QS",
        sizeCategory: "small",
        arrival: "20:15",
        departure: "21:15",
      },

      // Duplicate wave with slight offsets (+5-10 minutes, different tail numbers)
      {
        id: "ac37",
        tailNumber: "N132MX",
        sizeCategory: "small",
        arrival: "06:53",
        departure: "11:05",
      },
      {
        id: "ac38",
        tailNumber: "N822MX",
        sizeCategory: "small",
        arrival: "07:11",
        departure: "09:05",
      },
      {
        id: "ac39",
        tailNumber: "N682MX",
        sizeCategory: "small",
        arrival: "07:38",
        departure: "10:53",
      },
      {
        id: "ac40",
        tailNumber: "N697MX",
        sizeCategory: "small",
        arrival: "09:29",
        departure: "10:35",
      },
      {
        id: "ac41",
        tailNumber: "N478MX",
        sizeCategory: "small",
        arrival: "09:38",
        departure: "16:05",
      },
      {
        id: "ac42",
        tailNumber: "N523MX",
        sizeCategory: "small",
        arrival: "09:53",
        departure: "11:05",
      },
      {
        id: "ac43",
        tailNumber: "N839MX",
        sizeCategory: "small",
        arrival: "10:14",
        departure: "15:14",
      },
      {
        id: "ac44",
        tailNumber: "N791MX",
        sizeCategory: "small",
        arrival: "10:23",
        departure: "11:23",
      },
      {
        id: "ac45",
        tailNumber: "N336MX",
        sizeCategory: "small",
        arrival: "11:14",
        departure: "17:14",
      },
      {
        id: "ac46",
        tailNumber: "N866MX",
        sizeCategory: "small",
        arrival: "11:23",
        departure: "16:05",
      },
      {
        id: "ac47",
        tailNumber: "N313MX",
        sizeCategory: "small",
        arrival: "11:35",
        departure: "12:35",
      },
      {
        id: "ac48",
        tailNumber: "N957MX",
        sizeCategory: "small",
        arrival: "11:35",
        departure: "13:14",
      },
      {
        id: "ac49",
        tailNumber: "N525MX",
        sizeCategory: "small",
        arrival: "11:53",
        departure: "15:50",
      },
      {
        id: "ac50",
        tailNumber: "N881MX",
        sizeCategory: "small",
        arrival: "12:53",
        departure: "13:53",
      },
      {
        id: "ac51",
        tailNumber: "N91MX",
        sizeCategory: "small",
        arrival: "13:05",
        departure: "14:05",
      },
      {
        id: "ac52",
        tailNumber: "N922MX",
        sizeCategory: "small",
        arrival: "13:11",
        departure: "15:35",
      },
      {
        id: "ac53",
        tailNumber: "N835MX",
        sizeCategory: "small",
        arrival: "13:38",
        departure: "15:05",
      },
      {
        id: "ac54",
        tailNumber: "N972MX",
        sizeCategory: "small",
        arrival: "13:59",
        departure: "14:59",
      },
      {
        id: "ac55",
        tailNumber: "N856MX",
        sizeCategory: "small",
        arrival: "14:41",
        departure: "18:05",
      },
      {
        id: "ac56",
        tailNumber: "N791MX",
        sizeCategory: "small",
        arrival: "14:53",
        departure: "17:05",
      },
      {
        id: "ac57",
        tailNumber: "N677MX",
        sizeCategory: "small",
        arrival: "15:14",
        departure: "17:05",
      },
      {
        id: "ac58",
        tailNumber: "N731MX",
        sizeCategory: "small",
        arrival: "15:59",
        departure: "19:35",
      },
      {
        id: "ac59",
        tailNumber: "N604MX",
        sizeCategory: "small",
        arrival: "16:02",
        departure: "21:14",
      },
      {
        id: "ac60",
        tailNumber: "N410MX",
        sizeCategory: "small",
        arrival: "16:08",
        departure: "17:08",
      },
      {
        id: "ac61",
        tailNumber: "N189MX",
        sizeCategory: "small",
        arrival: "16:26",
        departure: "19:05",
      },
      {
        id: "ac62",
        tailNumber: "N348MX",
        sizeCategory: "small",
        arrival: "16:29",
        departure: "20:14",
      },
      {
        id: "ac63",
        tailNumber: "N755MX",
        sizeCategory: "small",
        arrival: "16:47",
        departure: "17:05",
      },
      {
        id: "ac64",
        tailNumber: "N750MX",
        sizeCategory: "small",
        arrival: "17:14",
        departure: "18:22",
      },
      {
        id: "ac65",
        tailNumber: "N905MX",
        sizeCategory: "small",
        arrival: "17:23",
        departure: "18:23",
      },
      {
        id: "ac66",
        tailNumber: "N635MX",
        sizeCategory: "small",
        arrival: "17:41",
        departure: "19:05",
      },
      {
        id: "ac67",
        tailNumber: "N871MX",
        sizeCategory: "small",
        arrival: "18:17",
        departure: "21:45",
      },
      {
        id: "ac68",
        tailNumber: "N570MX",
        sizeCategory: "small",
        arrival: "18:23",
        departure: "18:53",
      },
      {
        id: "ac69",
        tailNumber: "N512MX",
        sizeCategory: "small",
        arrival: "18:23",
        departure: "19:23",
      },
      {
        id: "ac70",
        tailNumber: "N373MX",
        sizeCategory: "small",
        arrival: "18:59",
        departure: "21:05",
      },
      {
        id: "ac71",
        tailNumber: "N514MX",
        sizeCategory: "small",
        arrival: "19:23",
        departure: "22:05",
      },
      {
        id: "ac72",
        tailNumber: "N160MX",
        sizeCategory: "small",
        arrival: "20:20",
        departure: "21:20",
      },
    ],
    // With 72 aircraft on 24 spots, expect significant oversubscription
    // Peak periods (afternoon) may have 30-35 aircraft wanting spots simultaneously
    expectedPlaced: 50,
    expectedUnplaced: 22,
    startTime: "06:00",
    horizonHours: 16,
  },
  {
    id: "egress-trap-basic",
    name: "Egress Trap - Basic (Long Blocks Short)",
    description:
      "Tests egress blocking detection. Long-dwell aircraft arrives early and would trap short-turn aircraft deeper in row. Algorithm should avoid placing long-dwell at front spots. **6 spots (A01-A06), Use 0 buffer**.",
    rampLayout: "teb", // Uses 6-spot row from TEB layout
    aircraft: [
      // Long-dwell aircraft (08:00-20:00, 12 hour dwell)
      {
        id: "ac1",
        tailNumber: "N650GL",
        sizeCategory: "small",
        arrival: "08:00",
        departure: "20:00",
      },

      // Short-turn aircraft deeper in row (all depart before long-dwell)
      {
        id: "ac2",
        tailNumber: "N822QS",
        sizeCategory: "small",
        arrival: "08:30",
        departure: "10:00",
      }, // A02
      {
        id: "ac3",
        tailNumber: "N697QS",
        sizeCategory: "small",
        arrival: "09:00",
        departure: "11:00",
      }, // A03
      {
        id: "ac4",
        tailNumber: "N478QS",
        sizeCategory: "small",
        arrival: "09:30",
        departure: "12:00",
      }, // A04
      {
        id: "ac5",
        tailNumber: "N523QS",
        sizeCategory: "small",
        arrival: "10:00",
        departure: "13:00",
      }, // A05
      {
        id: "ac6",
        tailNumber: "N839QS",
        sizeCategory: "small",
        arrival: "10:30",
        departure: "14:00",
      }, // A06
    ],
    // With egress blocking: long-dwell should be placed at A06 (back of row)
    // Without egress blocking: might place at A01 (front), trapping all others
    expectedPlaced: 6,
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "egress-trap-resolved",
    name: "Egress Trap - Resolved (Short Front, Long Back)",
    description:
      "No egress conflicts when short-turn aircraft are in front and long-dwell aircraft are in back. All placements should succeed with no traps. **6 spots (A01-A06), Use 0 buffer**.",
    rampLayout: "teb",
    aircraft: [
      // Short-turn aircraft (arrive first, placed at front)
      {
        id: "ac1",
        tailNumber: "N822QS",
        sizeCategory: "small",
        arrival: "08:00",
        departure: "10:00",
      }, // A01
      {
        id: "ac2",
        tailNumber: "N697QS",
        sizeCategory: "small",
        arrival: "08:30",
        departure: "11:00",
      }, // A02
      {
        id: "ac3",
        tailNumber: "N478QS",
        sizeCategory: "small",
        arrival: "09:00",
        departure: "12:00",
      }, // A03

      // Long-dwell aircraft (arrive later, placed at back)
      {
        id: "ac4",
        tailNumber: "N650GL",
        sizeCategory: "small",
        arrival: "09:30",
        departure: "20:00",
      }, // A04
      {
        id: "ac5",
        tailNumber: "N88GX",
        sizeCategory: "small",
        arrival: "10:00",
        departure: "21:00",
      }, // A05
      {
        id: "ac6",
        tailNumber: "N7XXF",
        sizeCategory: "small",
        arrival: "10:30",
        departure: "22:00",
      }, // A06
    ],
    // All should place successfully - short-turns depart before long-dwells
    expectedPlaced: 6,
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "egress-unavoidable",
    name: "Egress Unavoidable - Forced Trap",
    description:
      "All spots occupied except front spot. New aircraft arrives and only available placement would trap others. Tests allowTrapping=true vs false. **Use 0 buffer**.",
    rampLayout: "small", // 5 spots: A01-A03, B01-B02
    aircraft: [
      // Pre-fill spots A02, A03, B01, B02 with short-turns
      {
        id: "ac1",
        tailNumber: "N822QS",
        sizeCategory: "small",
        arrival: "08:00",
        departure: "10:00",
      }, // A02
      {
        id: "ac2",
        tailNumber: "N697QS",
        sizeCategory: "small",
        arrival: "08:10",
        departure: "10:30",
      }, // A03
      {
        id: "ac3",
        tailNumber: "N478QS",
        sizeCategory: "small",
        arrival: "08:20",
        departure: "11:00",
      }, // B01
      {
        id: "ac4",
        tailNumber: "N523QS",
        sizeCategory: "small",
        arrival: "08:30",
        departure: "11:30",
      }, // B02

      // Late-arriving long-dwell - only A01 available, but traps A02 and A03
      {
        id: "ac5",
        tailNumber: "N650GL",
        sizeCategory: "small",
        arrival: "09:00",
        departure: "20:00",
      },
    ],
    // With allowTrapping=true: 5 placed (accepts trap)
    // With allowTrapping=false: 4 placed, 1 unplaced (rejects trap)
    expectedPlaced: 5, // Default: allowTrapping=true
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "overnight-48h",
    name: "Overnight 48-Hour - Multi-Day Planning",
    description:
      "48-hour planning window with aircraft spanning midnight boundaries. Tests day-wrap-around logic and multi-day dwells. **5 spots, Use 15min buffer**.",
    rampLayout: "small",
    aircraft: [
      // Day 1 morning arrivals
      {
        id: "ac1",
        tailNumber: "N101",
        sizeCategory: "small",
        arrival: "08:00",
        departure: "16:00",
      }, // Same day
      {
        id: "ac2",
        tailNumber: "N102",
        sizeCategory: "small",
        arrival: "10:00",
        departure: "18:00",
      }, // Same day

      // Day 1 evening arrivals that stay overnight
      {
        id: "ac3",
        tailNumber: "N103",
        sizeCategory: "small",
        arrival: "18:00",
        departure: "08:00",
      }, // Next morning (14h dwell)
      {
        id: "ac4",
        tailNumber: "N104",
        sizeCategory: "small",
        arrival: "20:00",
        departure: "10:00",
      }, // Next morning (14h dwell)

      // Day 2 morning arrivals
      {
        id: "ac5",
        tailNumber: "N105",
        sizeCategory: "small",
        arrival: "08:00",
        departure: "14:00",
      }, // Day 2 afternoon
      {
        id: "ac6",
        tailNumber: "N106",
        sizeCategory: "small",
        arrival: "12:00",
        departure: "20:00",
      }, // Day 2 evening

      // Long-term parker spanning both days
      {
        id: "ac7",
        tailNumber: "N107",
        sizeCategory: "small",
        arrival: "06:00",
        departure: "06:00",
      }, // Full 24h (Day 1 06:00 -> Day 2 06:00)
    ],
    expectedPlaced: 7,
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 48,
  },
  {
    id: "night-shift",
    name: "Night Shift - Wrap-Around Schedule",
    description:
      "12-hour night shift (18:00-06:00) with wrap-around to next day. Tests time conversion for schedules crossing midnight. **5 spots, Use 0 buffer**.",
    rampLayout: "small",
    aircraft: [
      // Evening arrivals
      {
        id: "ac1",
        tailNumber: "N201",
        sizeCategory: "small",
        arrival: "18:00",
        departure: "22:00",
      },
      {
        id: "ac2",
        tailNumber: "N202",
        sizeCategory: "small",
        arrival: "19:00",
        departure: "23:00",
      },
      {
        id: "ac3",
        tailNumber: "N203",
        sizeCategory: "small",
        arrival: "20:00",
        departure: "00:00",
      }, // Departs at midnight

      // Late night arrivals (after midnight)
      {
        id: "ac4",
        tailNumber: "N204",
        sizeCategory: "small",
        arrival: "00:00",
        departure: "04:00",
      },
      {
        id: "ac5",
        tailNumber: "N205",
        sizeCategory: "small",
        arrival: "02:00",
        departure: "06:00",
      },

      // All-nighter
      {
        id: "ac6",
        tailNumber: "N206",
        sizeCategory: "small",
        arrival: "18:00",
        departure: "06:00",
      }, // Full 12h shift
    ],
    expectedPlaced: 6,
    expectedUnplaced: 0,
    startTime: "18:00",
    horizonHours: 12,
  },
  {
    id: "weekend-72h",
    name: "Weekend 72-Hour - Extended Planning",
    description:
      "3-day planning window (Friday-Sunday) with varied patterns. Tests timeline rendering with multiple days and long-term parkers. **7 spots, Use 15min buffer**.",
    rampLayout: "medium",
    aircraft: [
      // Friday arrivals - short visits (buckets: 4 per hour, 96 per day)
      {
        id: "ac1",
        tailNumber: "N301",
        sizeCategory: "small",
        arrival: 32,
        departure: 64,
      }, // Fri 08:00-16:00
      {
        id: "ac2",
        tailNumber: "N302",
        sizeCategory: "small",
        arrival: 48,
        departure: 80,
      }, // Fri 12:00-20:00

      // Friday evening -> Saturday morning (overnight)
      {
        id: "ac3",
        tailNumber: "N303",
        sizeCategory: "small",
        arrival: 72,
        departure: 128,
      }, // Fri 18:00 -> Sat 08:00
      {
        id: "ac4",
        tailNumber: "N304",
        sizeCategory: "small",
        arrival: 80,
        departure: 136,
      }, // Fri 20:00 -> Sat 10:00

      // Weekend warriors (Fri -> Sat, Fri -> Sun)
      {
        id: "ac5",
        tailNumber: "N305",
        sizeCategory: "small",
        arrival: 56,
        departure: 152,
      }, // Fri 14:00 -> Sat 14:00 (24h)
      {
        id: "ac6",
        tailNumber: "N306",
        sizeCategory: "small",
        arrival: 64,
        departure: 256,
      }, // Fri 16:00 -> Sun 16:00 (48h)

      // Saturday activity
      {
        id: "ac7",
        tailNumber: "N307",
        sizeCategory: "small",
        arrival: 128,
        departure: 160,
      }, // Sat 08:00-16:00
      {
        id: "ac8",
        tailNumber: "N308",
        sizeCategory: "small",
        arrival: 144,
        departure: 176,
      }, // Sat 12:00-20:00

      // Sunday arrivals
      {
        id: "ac9",
        tailNumber: "N309",
        sizeCategory: "small",
        arrival: 224,
        departure: 256,
      }, // Sun 08:00-16:00
      {
        id: "ac10",
        tailNumber: "N310",
        sizeCategory: "small",
        arrival: 240,
        departure: 272,
      }, // Sun 12:00-20:00

      // Full weekend parker
      {
        id: "ac11",
        tailNumber: "N311",
        sizeCategory: "small",
        arrival: 24,
        departure: 280,
      }, // Fri 06:00 -> Sun 22:00 (64h)
    ],
    expectedPlaced: 11,
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 72,
  },
  {
    id: "gap-demo",
    name: "Gap Spot Demo - Phase 2d",
    description:
      "Demonstrates gap spot dependency checking. B01 and B02 are large spots, B06 is a small gap spot that requires BOTH B01 and B02 to be occupied. Phase 2d: B06 only available when dependencies are met. 2 large aircraft occupy B01/B02 (08:00-18:00), during this time the small aircraft (10:00-15:00) should be placed in B06. When only 1 large aircraft present, B06 should NOT be available.",
    rampLayout: "gap-demo",
    aircraft: [
      // Large aircraft occupy B01 and B02 (full day)
      {
        id: "ac1",
        tailNumber: "N700L",
        sizeCategory: "large",
        arrival: "08:00",
        departure: "18:00",
      },
      {
        id: "ac2",
        tailNumber: "N701L",
        sizeCategory: "super-midsize",
        arrival: "09:00",
        departure: "17:00",
      },
      // Small aircraft should fit in gap spot B06 when B01 and B02 are both occupied
      {
        id: "ac3",
        tailNumber: "N702S",
        sizeCategory: "small",
        arrival: "10:00",
        departure: "12:00",
      },
      {
        id: "ac4",
        tailNumber: "N703S",
        sizeCategory: "very-small",
        arrival: "13:00",
        departure: "15:00",
      },
    ],
    expectedPlaced: 4, // Phase 2d: All 4 should place (dependencies met when both large aircraft present)
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "lock-vip-maintenance",
    name: "VIP Aircraft - Maintenance Lock",
    description:
      "VIP Gulfstream G650 requires maintenance in hangar spot A01 from 12:00-14:00. Aircraft arrives 08:00, locked to A01 for maintenance, then moves to regular spot after. Tests mid-dwell lock with 3 segments (requires move cap ≥ 2).",
    rampLayout: "medium",
    aircraft: [
      {
        id: "vip1",
        tailNumber: "N650VIP",
        sizeCategory: "super-midsize",
        arrival: "08:00",
        departure: "18:00",
        locks: [
          {
            spotId: "A01",
            startBucket: 48,
            endBucket: 56,
            reason: "maintenance",
          },
        ],
      },
      // Add some background traffic to make it interesting
      {
        id: "ac2",
        tailNumber: "N201",
        sizeCategory: "small",
        arrival: "09:00",
        departure: "11:00",
      },
      {
        id: "ac3",
        tailNumber: "N202",
        sizeCategory: "small",
        arrival: "13:00",
        departure: "15:00",
      },
      {
        id: "ac4",
        tailNumber: "N203",
        sizeCategory: "midsize",
        arrival: "16:00",
        departure: "20:00",
      },
    ],
    expectedPlaced: 4,
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "lock-conflict-demo",
    name: "Lock Conflict - Spot Unavailable",
    description:
      "Two aircraft both want spot A01, but first arrives earlier and takes it. Second aircraft has a lock to A01 from 10:00-12:00, but A01 is occupied by first aircraft. Tests lock validation failure (spot occupied during lock period). Works with any move cap.",
    rampLayout: "medium",
    aircraft: [
      // First aircraft takes A01 from 08:00-14:00 (no lock)
      {
        id: "ac1",
        tailNumber: "N101",
        sizeCategory: "midsize",
        arrival: "08:00",
        departure: "14:00",
      },
      // Second aircraft wants A01 locked from 10:00-12:00, but it's occupied!
      {
        id: "ac2",
        tailNumber: "N102",
        sizeCategory: "midsize",
        arrival: "09:00",
        departure: "13:00",
        locks: [
          {
            spotId: "A01",
            startBucket: 40,
            endBucket: 48,
            reason: "vip",
          },
        ],
      },
      // Third aircraft should place fine (background traffic)
      {
        id: "ac3",
        tailNumber: "N103",
        sizeCategory: "small",
        arrival: "15:00",
        departure: "17:00",
      },
    ],
    expectedPlaced: 2, // ac1 and ac3 succeed, ac2 fails due to lock conflict
    expectedUnplaced: 1,
    startTime: "00:00",
    horizonHours: 24,
  },
  {
    id: "lock-multiple-moves",
    name: "Multiple Locks - Fueling + Boarding",
    description:
      "Heavy jet requires both fueling (A01: 10:00-11:00) and passenger boarding (B01: 14:00-15:00). Tests multiple non-overlapping locks creating 5 segments and 4 moves. Demonstrates complex multi-lock scenario. Requires move cap ≥ 4.",
    rampLayout: "medium",
    aircraft: [
      {
        id: "heavy1",
        tailNumber: "N777HA",
        sizeCategory: "large",
        arrival: "08:00",
        departure: "18:00",
        locks: [
          {
            spotId: "A01",
            startBucket: 40,
            endBucket: 44,
            reason: "fueling",
          },
          {
            spotId: "B01",
            startBucket: 56,
            endBucket: 60,
            reason: "boarding",
          },
        ],
      },
      // Background traffic
      {
        id: "ac2",
        tailNumber: "N301",
        sizeCategory: "small",
        arrival: "09:00",
        departure: "12:00",
      },
      {
        id: "ac3",
        tailNumber: "N302",
        sizeCategory: "small",
        arrival: "13:00",
        departure: "16:00",
      },
    ],
    expectedPlaced: 3,
    expectedUnplaced: 0,
    startTime: "00:00",
    horizonHours: 24,
  },
];

/**
 * Convert TestAircraft (time strings or buckets) to Aircraft (buckets)
 * @param testAircraft - Aircraft with time strings or bucket numbers
 * @param startTime - Planning window start time for bucket conversion
 */
function convertTestAircraftToAircraft(
  testAircraft: TestAircraft,
  startTime: string
): ParkingRequest {
  return {
    id: testAircraft.id,
    tail_number: testAircraft.tailNumber,
    sizeCategory: testAircraft.sizeCategory as any,
    arrivalBucket:
      typeof testAircraft.arrival === "number"
        ? testAircraft.arrival
        : timeStringToBucket(testAircraft.arrival, startTime),
    departureBucket:
      typeof testAircraft.departure === "number"
        ? testAircraft.departure
        : timeStringToBucket(testAircraft.departure, startTime),
    locks: testAircraft.locks,
  };
}

/**
 * Convert test scenario definitions (time strings) to runtime scenarios (buckets)
 */
function convertScenarioDefinitions(): TestScenario[] {
  return TEST_SCENARIO_DEFINITIONS.map((def) => ({
    ...def,
    aircraft: def.aircraft.map((ac) =>
      convertTestAircraftToAircraft(ac, def.startTime)
    ),
  }));
}

/**
 * Exported test scenarios with converted bucket times
 */
export const TEST_SCENARIOS: TestScenario[] = convertScenarioDefinitions();

/**
 * Get a fresh copy of test data with empty bitmaps
 * @param scenarioId - ID of the test scenario to load (defaults to "basic")
 */
export function getFreshTestData(scenarioId: string = "basic") {
  const scenario =
    TEST_SCENARIOS.find((s) => s.id === scenarioId) || TEST_SCENARIOS[0];
  const layout = LAYOUTS[scenario.rampLayout];

  return {
    zones: [...layout.zones],
    spots: layout.spots.map((spot) => ({
      ...spot,
      occupancy: makeEmptyMask(scenario.horizonHours),
    })),
    aircraft: [...scenario.aircraft],
    scenario,
    horizonHours: scenario.horizonHours,
    startTime: scenario.startTime,
  };
}
